import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import styles from "./BucketsOfAllocation.module.css";

const SORTABLE_COLUMNS = Object.freeze([
  {
    label: "BG",
    sortBy: "BG",
    content: "BG",
  },
  {
    label: "Small C",
    sortBy: "Small C",
    content: "Small C",
  },
  {
    label: "Source",
    sortBy: "Source",
    content: "Source",
  },
  {
    label: "Order Loss Post PDQ",
    sortBy: "Order Loss Post PDQ",
    content: (
      <>
        Order loss <br />
        post PDQ
      </>
    ),
  },
  {
    label: "NR Loss",
    sortBy: "NR Loss",
    content: "NR Loss",
  },
  {
    label: "ATP Stock %",
    sortBy: "ATP Stock %",
    content: (
      <>
        ATP <br />
        Stock %
      </>
    ),
  },
  {
    label: "QC Unlock %",
    sortBy: "QC Unlock %",
    content: "QC Unlock %",
  },
  {
    label: "Quality Pending %",
    sortBy: "Quality Pending %",
    content: "Quality Pending %",
  },
  {
    label: "Prod Plan %",
    sortBy: "Prod Plan %",
    content: "Prod Plan %",
  },
]);

const getSortAriaLabel = (column, sortingData) => {
  if (sortingData?.sortBy !== column.sortBy) {
    return `Sort by ${column.label} ascending`;
  }

  if (sortingData?.sortOrder === "ASC") {
    return `Sort by ${column.label} descending`;
  }

  return `Sort by ${column.label} ascending`;
};

const SortIcon = React.memo(({ isActive, sortDirection }) => {
  if (isActive) {
    if (sortDirection === "ASC") {
      return <ArrowDropUpIcon sx={{ fontSize: 18 }} />;
    }

    return <ArrowDropDownIcon sx={{ fontSize: 18 }} />;
  }

  return (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        opacity: 0.5,
      }}
    >
      <ArrowDropUpIcon
        sx={{
          fontSize: 18,
          marginBottom: "-8px",
        }}
      />
      <ArrowDropDownIcon sx={{ fontSize: 18 }} />
    </span>
  );
});

SortIcon.propTypes = {
  isActive: PropTypes.bool.isRequired,
  sortDirection: PropTypes.string.isRequired,
};

SortIcon.displayName = "SortIcon";

const SortableHeaderCell = React.memo(({ column, sortingData, onSortChange }) => {
  const isActive = sortingData?.sortBy === column.sortBy;

  const handleClick = () => {
    if (onSortChange) {
      onSortChange(column.sortBy);
    }
  };

  return (
    <th>
      <button
        type="button"
        onClick={handleClick}
        aria-label={getSortAriaLabel(column, sortingData)}
        style={{
          width: "100%",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "inherit",
          font: "inherit",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          padding: 0,
        }}
      >
        <span>{column.content}</span>
        <SortIcon
          isActive={isActive}
          sortDirection={sortingData?.sortOrder || "DESC"}
        />
      </button>
    </th>
  );
});

SortableHeaderCell.propTypes = {
  column: PropTypes.shape({
    label: PropTypes.string.isRequired,
    sortBy: PropTypes.string.isRequired,
    content: PropTypes.node.isRequired,
  }).isRequired,
  sortingData: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }).isRequired,
  onSortChange: PropTypes.func,
};

SortableHeaderCell.defaultProps = {
  onSortChange: undefined,
};

SortableHeaderCell.displayName = "SortableHeaderCell";

const formatNumericDisplay = (value) => {
  const num = Number(value);

  if (Number.isNaN(num)) {
    return value ?? "";
  }

  return num.toFixed(2);
};

const getDecimalDigitsCount = (value) => {
  const valueStr = String(value).trim();

  if (!valueStr || valueStr.toLowerCase() === "nan") {
    return 0;
  }

  if (!/[eE]/.test(valueStr)) {
    const decimalPart = valueStr.split(".")[1];
    return decimalPart ? decimalPart.length : 0;
  }

  const [mantissa, exponentPart] = valueStr.toLowerCase().split("e");
  const exponent = Number.parseInt(exponentPart, 10);
  const mantissaDecimals = (mantissa.split(".")[1] || "").length;

  if (Number.isNaN(exponent)) {
    return 0;
  }

  return exponent >= 0
    ? Math.max(0, mantissaDecimals - exponent)
    : mantissaDecimals + Math.abs(exponent);
};

const getNrLossPresentation = (value) => {
  if (value === null || value === undefined || value === "") {
    return {
      displayValue: value ?? "",
      tooltip: undefined,
    };
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return {
      displayValue: value,
      tooltip: undefined,
    };
  }

  if (Object.is(num, 0)) {
    return {
      displayValue: "0",
      tooltip: undefined,
    };
  }

  const decimalDigitsCount = getDecimalDigitsCount(value);

  if (decimalDigitsCount > 2) {
    return {
      displayValue: formatNumericDisplay(value),
      tooltip: `Original: ${value}`,
    };
  }

  return {
    displayValue: value,
    tooltip: undefined,
  };
};

const BucketsOfAllocation = ({
  apiData,
  loading,
  loadMoreLoading = false,
  hasMore = false,
  onLoadMore,
  showExpanded,
  sortingData,
  onSortChange,
}) => {
  const data = apiData?.data || [];

  const observerRef = useRef(null);
  const preloadRowRef = useRef(null);

  useEffect(() => {
    if (
      loading ||
      loadMoreLoading ||
      !hasMore ||
      !onLoadMore ||
      !preloadRowRef.current
    ) {
      return undefined;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        if (
          firstEntry.isIntersecting &&
          hasMore &&
          !loading &&
          !loadMoreLoading
        ) {
          onLoadMore();
        }
      },
      {
        root: null,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(preloadRowRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [data, loading, loadMoreLoading, hasMore, onLoadMore]);

  const preloadIndex = data.length >= 15 ? data.length - 3 : data.length - 1;

  return (
    <div
      className={`${styles.tableContainer} ${
        showExpanded
          ? styles.tableContainerheight
          : styles.tableContainerPopupheight
      }`}
    >
      <table className={styles.table}>
        <thead>
          <tr>
            {SORTABLE_COLUMNS.map((column) => (
              <SortableHeaderCell
                key={column.sortBy}
                column={column}
                sortingData={sortingData}
                onSortChange={onSortChange}
              />
            ))}
          </tr>
        </thead>

        <tbody>
          {loading ? (
            Array.from({ length: 8 }).map((_, rowNumber) => (
              <tr key={`initial-loading-row-${rowNumber + 1}`}>
                {Array.from({ length: 9 }).map((__, cellNumber) => (
                  <td
                    key={`initial-loading-cell-${rowNumber + 1}-${cellNumber + 1}`}
                  >
                    <div className={styles.shimmer}></div>
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <>
              {data.map((row, index) => {
                const { displayValue: nrLossDisplay, tooltip: nrLossTooltip } = getNrLossPresentation(
                  row["NR Loss"],
                );

                const orderLossValue = row["Order Loss Post PDQ"];
                const formattedOrderLoss = orderLossValue !== null && orderLossValue !== undefined && orderLossValue !== ""
                  ? formatNumericDisplay(orderLossValue)
                  : orderLossValue ?? "";

                const rowKey = [
                  row["BG"] || "",
                  row["Small C"] || "",
                  row["Source"] || "",
                  row["Order Loss Post PDQ"] || "",
                  row["NR Loss"] || "",
                ].join("-");

                return (
                  <tr
                    key={rowKey}
                    ref={index === preloadIndex ? preloadRowRef : null}
                  >
                    <td>{row["BG"]}</td>
                    <td>{row["Small C"]}</td>
                    <td>{row["Source"]}</td>
                    <td>{formattedOrderLoss}</td>

                    <td title={nrLossTooltip}>{nrLossDisplay}</td>

                    <td className={styles.highlightCell}>
                      {row["ATP Stock %"]}%
                    </td>

                    <td className={styles.highlightCell}>
                      {row["QC Unlock %"]}%
                    </td>

                    <td className={styles.highlightCell}>
                      {row["Quality Pending %"]}%
                    </td>

                    <td className={styles.highlightCell}>
                      {row["Prod Plan %"]}%
                    </td>
                  </tr>
                );
              })}

              {loadMoreLoading &&
                Array.from({ length: 3 }).map((_, rowNumber) => (
                  <tr key={`load-more-row-${rowNumber + 1}`}>
                    {Array.from({ length: 9 }).map((__, cellNumber) => (
                      <td
                        key={`load-more-cell-${rowNumber + 1}-${cellNumber + 1}`}
                      >
                        <div className={styles.shimmer}></div>
                      </td>
                    ))}
                  </tr>
                ))}

              {!loadMoreLoading && data.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      fontSize: "12px",
                    }}
                  >
                    No data found
                  </td>
                </tr>
              )}

              {!loadMoreLoading && data.length > 0 && !hasMore && (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: "center",
                      padding: "8px",
                      fontSize: "12px",
                      color: "#6B7280",
                    }}
                  >
                    No more records
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

BucketsOfAllocation.propTypes = {
  apiData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object),
  }),
  loading: PropTypes.bool,
  loadMoreLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  showExpanded: PropTypes.bool,
  sortingData: PropTypes.shape({
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }),
  onSortChange: PropTypes.func,
};

BucketsOfAllocation.defaultProps = {
  apiData: {
    data: [],
  },
  loading: false,
  loadMoreLoading: false,
  hasMore: false,
  onLoadMore: undefined,
  showExpanded: false,
  sortingData: {
    sortBy: "BG",
    sortOrder: "DESC",
  },
  onSortChange: undefined,
};

export default BucketsOfAllocation;

